/**
 * Pure validation helpers for activity data.
 *
 * Shared by the bulk-import parser (parsing XLSX rows) and the multi-row
 * entry table (validating draft rows before submit). Keep these stateless
 * and side-effect-free — they're pure functions over `@g17eco/emissions-config`
 * data, so we can use them anywhere a row needs to be checked.
 */

import {
  EmissionScope,
  Scope1ActivityType,
  Scope2ActivityType,
  ActivityUnit,
  FuelType,
  VehicleType,
  ProcessType,
  getStationaryFuels,
  getMobileFuels,
  getValidUnits,
  type ActivityTypeWithUnits,
} from '../types/emissions';
import { VALID_GASES } from '@g17eco/emissions-config';

export interface ValidationError {
  field: string;
  message: string;
}

export const isValidActivityType = (activityType: string, scope: EmissionScope): boolean => {
  const validTypes: string[] =
    scope === EmissionScope.Scope1
      ? Object.values(Scope1ActivityType)
      : Object.values(Scope2ActivityType);
  return validTypes.includes(activityType);
};

export const isValidUnit = (unit: string): boolean =>
  Object.values(ActivityUnit).includes(unit as ActivityUnit);

export const isUnitValidForActivity = (
  unit: string,
  activityType: string,
  fuelType?: string,
): boolean => {
  const validUnits = getValidUnits(
    activityType as ActivityTypeWithUnits,
    (fuelType as FuelType) || undefined,
  );
  if (validUnits.length === 0) {
    // Unknown activity type — caller will surface a different error first
    return true;
  }
  return validUnits.includes(unit as ActivityUnit);
};

export const isValidFuelType = (fuelType: string, activityType?: string): boolean => {
  if (!fuelType) {
    return true;
  }
  if (activityType === Scope1ActivityType.StationaryCombustion) {
    return (getStationaryFuels() as string[]).includes(fuelType);
  }
  if (activityType === Scope1ActivityType.MobileCombustion) {
    return (getMobileFuels() as string[]).includes(fuelType);
  }
  return Object.values(FuelType).includes(fuelType as FuelType);
};

export const isValidVehicleType = (vehicleType: string): boolean =>
  !vehicleType || Object.values(VehicleType).includes(vehicleType as VehicleType);

/**
 * Either kind of gas is valid in the wizard's combined "gas" picker
 * (refrigerants R134A/R32/etc. AND industrial fugitives SF6/NF3/PFCs).
 * The submit-time routing splits them back into the route's distinct
 * `refrigerantType` / `gasType` fields via `isFugitiveGas` (re-exported
 * from `@g17eco/emissions-config`). Same source of truth as the asset
 * import-template's "Gas Type" dropdown — drift-proof against the
 * underlying enums.
 */
export const isValidFugitiveGasType = (gasType: string): boolean =>
  !gasType || VALID_GASES.includes(gasType as never);

export const isValidRefrigerantType = (refrigerantType: string): boolean =>
  !refrigerantType || VALID_GASES.includes(refrigerantType as never);

export const isValidProcessType = (processType: string): boolean =>
  !processType || Object.values(ProcessType).includes(processType as ProcessType);

// Re-exported from `@g17eco/emissions-config` so every consumer of
// the routing decision (wizard submit, factor-search hook, bulk
// parser) reads from one source.
export { isFugitiveGas } from '@g17eco/emissions-config';

/**
 * Period dates accepted from the form are 'YYYY-MM' (HTML month input) or
 * 'YYYY-MM-DD' (XLSX/parser-normalised). Both are valid; we just need a
 * sanity check that what's there can be parsed.
 */
export const isValidPeriodDate = (value: string): boolean => {
  if (!value) {
    return false;
  }
  const monthRegex = /^\d{4}-\d{2}$/;
  const dayRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!monthRegex.test(value) && !dayRegex.test(value)) {
    return false;
  }
  return !Number.isNaN(new Date(value).getTime());
};

/**
 * True iff `start <= end` after coercing both to dates.
 */
export const isValidPeriodRange = (start: string, end: string): boolean => {
  if (!isValidPeriodDate(start) || !isValidPeriodDate(end)) {
    return false;
  }
  return new Date(start).getTime() <= new Date(end).getTime();
};

/**
 * Field-level required-checks for a draft row, scoped to the chosen
 * activity type. Returns an array of `ValidationError` (empty when the
 * row is fully valid).
 *
 * Used by the multi-row entry table to:
 *   (a) gate the Submit button (any row with errors blocks submit), and
 *   (b) drive inline error highlights per field.
 */
export interface DraftRowFields {
  scope?: EmissionScope | '';
  activityType?: string;
  periodStart?: string;
  periodEnd?: string;
  quantity?: string;
  unit?: string;
  region?: string;
  fuelType?: string;
  vehicleType?: string;
  refrigerantType?: string;
  processType?: string;
}

export const validateDraftRow = (row: DraftRowFields): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!row.scope) {
    errors.push({ field: 'scope', message: 'Scope is required' });
  }

  if (!row.activityType) {
    errors.push({ field: 'activityType', message: 'Activity type is required' });
  } else if (row.scope && !isValidActivityType(row.activityType, row.scope)) {
    errors.push({ field: 'activityType', message: 'Activity type is not valid for the chosen scope' });
  }

  if (!isValidPeriodRange(row.periodStart ?? '', row.periodEnd ?? '')) {
    errors.push({ field: 'period', message: 'Provide a valid period start and end (end must not precede start)' });
  }

  const quantityNum = Number(row.quantity);
  if (row.quantity === undefined || row.quantity === '' || Number.isNaN(quantityNum) || quantityNum < 0) {
    errors.push({ field: 'quantity', message: 'Quantity must be a non-negative number' });
  }

  if (!row.unit) {
    errors.push({ field: 'unit', message: 'Unit is required' });
  } else if (row.activityType && !isUnitValidForActivity(row.unit, row.activityType, row.fuelType)) {
    errors.push({ field: 'unit', message: 'Unit is not valid for this activity/fuel combination' });
  }

  if (!row.region) {
    errors.push({ field: 'region', message: 'Region is required' });
  }

  // Subtype required-by-type checks
  if (row.activityType === Scope1ActivityType.StationaryCombustion && !row.fuelType) {
    errors.push({ field: 'fuelType', message: 'Fuel type is required for stationary combustion' });
  } else if (row.fuelType && !isValidFuelType(row.fuelType, row.activityType)) {
    errors.push({ field: 'fuelType', message: 'Fuel type is not valid for this activity type' });
  }

  if (row.activityType === Scope1ActivityType.MobileCombustion && !row.vehicleType) {
    errors.push({ field: 'vehicleType', message: 'Vehicle type is required for mobile combustion' });
  } else if (row.vehicleType && !isValidVehicleType(row.vehicleType)) {
    errors.push({ field: 'vehicleType', message: 'Vehicle type is not valid' });
  }

  if (row.activityType === Scope1ActivityType.FugitiveEmissions && !row.refrigerantType) {
    errors.push({ field: 'refrigerantType', message: 'Gas / refrigerant type is required for fugitive emissions' });
  } else if (row.refrigerantType && !isValidRefrigerantType(row.refrigerantType)) {
    errors.push({ field: 'refrigerantType', message: 'Gas / refrigerant type is not valid' });
  }

  if (row.activityType === Scope1ActivityType.ProcessEmissions && !row.processType) {
    errors.push({ field: 'processType', message: 'Process type is required for process emissions' });
  } else if (row.processType && !isValidProcessType(row.processType)) {
    errors.push({ field: 'processType', message: 'Process type is not valid' });
  }

  return errors;
};
